import { requestPostTailorMadeTourRequest } from 'api/tourV2'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import TextArea from 'components/Common/Form/Input/TextArea'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import Divider from 'components/Luxkit/Divider'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { rem } from 'polished'
import React, { FormEventHandler, useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import TailorMadeTourAcknowledgementInput from './TailorMadeTourAcknowledgementInput'
import TailorMadeTourBudgetInput, { parseTailorMadeTourBudgetValue } from './TailorMadeTourBudgetInput'
import TailorMadeTourDatesInput, { parseTailorMadeTourDatesValue } from './TailorMadeTourDatesInput'
import TailorMadeTourDestinationsInput, { parseTailorMadeTourDestinationsValue } from './TailorMadeTourDestinationsInput'
import TailorMadeTourFirstEmailInput from './TailorMadeTourEmailInput'
import TailorMadeTourFirstNameInput from './TailorMadeTourFirstNameInput'
import TailorMadeTourFormStepTitle from './TailorMadeTourFormFieldset'
import TailorMadeTourHomeCountryInput from './TailorMadeTourHomeCountryInput'
import TailorMadeTourLastNameInput from './TailorMadeTourLastNameInput'
import TailorMadeTourLengthInput, { parseTailorMadeTourLengthValue } from './TailorMadeTourLengthInput'
import TailorMadeTourOccupantsInputs from './TailorMadeTourOccupantsInputs'
import TailorMadeTourPreferredCallbackTimeInput, { parseTailorMadeTourPreferredCallbackTimeValue } from './TailorMadeTourPreferredCallbackTimeInput'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import { selectLoggedIn } from 'selectors/accountSelectors'

const Container = styled.form`
  position: relative; /* so that the loading indicator overlay can stick to the right place */
`

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: end;
  row-gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    column-gap: ${rem(16)};
  }
`

const InputNames = {
  destinations: 'destinations',
  destinationsEditor: 'destinationsEditor',
  travelDates: 'travelDates',
  travelLength: 'travelLength',
  budget: 'budget',
  adultsCount: 'adultsCount',
  childrenCount: 'childrenCount',
  message: 'message',
  contactFirstName: 'contactFirstName',
  contactLastName: 'contactLastName',
  contactEmail: 'contactEmail',
  contactPhone: 'contactPhone',
  contactCountry: 'contactCountry',
  preferredCallbackTime: 'preferredCallbackTime',
} as const

interface TailorMadeTourRequestFormValues {
  destinations: string
  destinationsEditor: string
  travelDates: string
  travelLength: string
  budget: string
  adultsCount: string
  childrenCount: string
  message: string
  contactFirstName: string
  contactLastName: string
  contactEmail: string
  contactPhone: App.PhoneNumber
  contactCountry: string
  preferredCallbackTime: string
}

interface Props {
  tourOffer?: App.Tours.TourV2Offer
  tourOfferVariation?: App.Tours.TourV2OfferVariation
  onSubmit: (requestId: string) => void
}

function TailorMadeTourRequestForm({ tourOffer, tourOfferVariation, onSubmit }: Props) {
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const showModal = useContext(ModalContext)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const [submission, setSubmission] = useState<App.StatefulData<{ request: App.Tours.TailorMadeTourRequest, requestId: string }>>({ initial: true })
  const submitRequest = useCallback(async(request: App.Tours.TailorMadeTourRequest) => {
    const canSubmit = isLoggedIn || ((await showModal<AccountAccessModalResult>(<AccountAccessModal />)).loggedIn)
    if (canSubmit) {
      setSubmission({ fetching: true })
      try {
        const requestId = await requestPostTailorMadeTourRequest(
          request,
          { region: currentRegionCode },
        )
        setSubmission({ fetching: false, data: { request, requestId } })
      } catch (error) {
        showSnackbar('There was a problem with submitting your tailor made tour request. Please try again.', 'critical')
        setSubmission({ fetching: false, error: String(error) })
      }
    }
  }, [currentRegionCode, isLoggedIn, showModal])
  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>(async(event) => {
    event.preventDefault()
    const formData = formToObject<TailorMadeTourRequestFormValues>(event.currentTarget)
    const request: App.Tours.TailorMadeTourRequest = {
      tourOfferId: tourOffer?.id,
      tourOptionId: tourOfferVariation?.fkTourOptionId,
      destinations: parseTailorMadeTourDestinationsValue(formData[InputNames.destinations]),
      travelDates: parseTailorMadeTourDatesValue(formData[InputNames.travelDates]),
      travelLength: parseTailorMadeTourLengthValue(formData[InputNames.travelLength]),
      budget: parseTailorMadeTourBudgetValue(formData[InputNames.budget]),
      travellers: {
        adults: Number(formData[InputNames.adultsCount]),
        children: Number(formData[InputNames.childrenCount]),
      },
      message: formData[InputNames.message],
      contactFirstName: formData[InputNames.contactFirstName],
      contactLastName: formData[InputNames.contactLastName],
      contactEmail: formData[InputNames.contactEmail],
      contactPhone: {
        prefix: formData[InputNames.contactPhone].prefix,
        number: formData[InputNames.contactPhone].phone,
      },
      contactCountry: formData[InputNames.contactCountry],
      preferredCallbackTime: parseTailorMadeTourPreferredCallbackTimeValue(formData[InputNames.preferredCallbackTime]),
    }
    submitRequest(request)
  }, [submitRequest, tourOffer?.id, tourOfferVariation?.fkTourOptionId])

  useEffect(() => {
    if (submission.data) {
      onSubmit(submission.data.requestId)
    }
  }, [onSubmit, submission.data])

  return <Container id="tailor-made-tour-form" onSubmit={handleSubmission}>
    <LoadingIndicator visible={!!submission.fetching} floating />
    <Group direction="vertical" gap={32} tabletGap={40}>
      <Group direction="vertical" gap={24}>
        <TailorMadeTourFormStepTitle step={1} title="Choose your destinations">
          <FieldsGrid>
            <TailorMadeTourDestinationsInput
              name={InputNames.destinations}
              tourOfferVariation={tourOfferVariation}
            />
          </FieldsGrid>
        </TailorMadeTourFormStepTitle>
        <Divider kind="secondary" />
        <TailorMadeTourFormStepTitle step={2} title="Select your preferred travel dates">
          <FieldsGrid>
            <TailorMadeTourDatesInput name={InputNames.travelDates} />
            <TailorMadeTourLengthInput name={InputNames.travelLength} />
          </FieldsGrid>
        </TailorMadeTourFormStepTitle>
        <Divider kind="secondary" />
        <TailorMadeTourFormStepTitle step={3} title="Set your overall budget">
          <FieldsGrid>
            <TailorMadeTourBudgetInput name={InputNames.budget} />
          </FieldsGrid>
        </TailorMadeTourFormStepTitle>
        <Divider kind="secondary" />
        <TailorMadeTourFormStepTitle step={4} title="Select number of travellers">
          <FieldsGrid>
            <TailorMadeTourOccupantsInputs
              adultsInputName={InputNames.adultsCount}
              childrenInputName={InputNames.childrenCount}
            />
          </FieldsGrid>
        </TailorMadeTourFormStepTitle>
        <Divider kind="secondary" />
        <TailorMadeTourFormStepTitle step={5} title="Any other comments or questions?">
          <TextArea
            name={InputNames.message}
            label="Leave a message"
            placeholder="e.g. I wish to include flights..."
          />
        </TailorMadeTourFormStepTitle>
        <Divider kind="secondary" />
        <TailorMadeTourFormStepTitle step={6} title="Your contact details">
          <Group direction="vertical" gap={8}>
            <FieldsGrid>
              <TailorMadeTourFirstNameInput name={InputNames.contactFirstName} />
              <TailorMadeTourLastNameInput name={InputNames.contactLastName} />
            </FieldsGrid>
            <TailorMadeTourFirstEmailInput name={InputNames.contactEmail} />
            <FieldsGrid>
              <PhoneInput
                name={InputNames.contactPhone}
                required
                label="Phone number"
              />
              <TailorMadeTourHomeCountryInput name={InputNames.contactCountry} />
              <TailorMadeTourPreferredCallbackTimeInput name={InputNames.preferredCallbackTime} />
            </FieldsGrid>
          </Group>
        </TailorMadeTourFormStepTitle>
      </Group>
      <TailorMadeTourAcknowledgementInput />
      <Group direction="vertical" tabletDirection="horizontal-reverse">
        <TextButton kind="primary" type="submit">Submit request</TextButton>
      </Group>
    </Group>
  </Container>
}

export default TailorMadeTourRequestForm
